import { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";

import FooterText from "../../components/footerText";
import HubInputField from "../../components/inputType";
import ContactAndSafetyInfo from "src/components/contactAndSafetyInfo";

import { AuthContext } from "../../context/authContext";
import { ToastContext } from "../../context/toastContext";
import useFetch from "../../hooks/useFetch";

import { EuflexxaBanner } from "src/assets";
import { routeToRelevantUser } from "src/helpers";
import { LoginSchema } from "../../validation/login";
import { APIEndpoints } from "../../types/apiTypes";
import { LoginFormType, LoginAPIResponseType } from "./types";
import { ToastTypes } from "../../types";
import { TextAlignProps } from "src/components/contactAndSafetyInfo/types";




const HubLogin: FC = (): JSX.Element => {
  const [isPasswordVisible, setPasswordVisibility] = useState<boolean>(false);

  const { control, handleSubmit } = useForm({
    mode: 'all',
    resolver: yupResolver(LoginSchema),
  });
  const { userData: sessionSavedUser, createSession, restoreSession } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate()




  const {
    data: userData, loading, mutate,
    error, removeErrorAndData
  } = useFetch<LoginAPIResponseType>(APIEndpoints.USER_SIGNIN);

  useEffect(() => {
    if (userData && userData.status.code === 200) {
      const {status, token} = userData;
      createSession(
        {
          ...status.data.user,
          practice_created_date: status.data.address ? status.data.address.date : "",
          practice_name: status.data.address ? status.data.address.name : "",
          address: status.data.address ? status.data.address.ref_address : "",
          reset_demo: status.data.sub_header ? status.data.sub_header.reset_demo : false,
          ...(status.data.navbar && status.data.navbar.retreatment_reminders ? {
              retreatment_reminders: status.data.navbar.retreatment_reminders.value === "0" ? false : true
            } : {
              retreatment_reminders: false,
            })
        },
        status.data.sub_header,
        token
      );
      showToast([status.message], ToastTypes.INFO);

      const route = routeToRelevantUser(status.data.user);
      navigate(route)
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const submitForm = async (values: LoginFormType) => {
    if ((error && error.length > 0) || userData) removeErrorAndData();
    const isSessionExists = restoreSession();

    if (!isSessionExists) {
      mutate({ user: values });
    } else {
      showToast([`There is already an active user session`], ToastTypes.INFO);
      if (sessionSavedUser) {
        const route = routeToRelevantUser(sessionSavedUser);
      navigate(route)
      }
    }
  };




  return (
    <div className="container-fluid login-sec d-flex flex-fill flex-column justify-content-between overflow-auto">
      <div className="container d-flex flex-fill flex-column">
        <div className="card mt-2">
          <div className="card-body p-2">
            <div className="row">
              <div className="col-12 col-md-6">
                <img className="img-fluid login-banner" src={EuflexxaBanner} alt="Banner Info"/>
              </div>
              <div className="col-12 col-md-6">
                <ContactAndSafetyInfo alignText={TextAlignProps.TEXT_END} textColor="black"/>
              </div>
            </div>
          </div>
        </div>

        <div className="row flex-fill pt-2">
          <div className="col-12 col-md-7"></div>

          <div className="col-12 col-md-5">
            <form className="card mt-4" onSubmit={handleSubmit(submitForm)}>
              <div className="card-header">
                <h3 className="mb-1 text-white fw-medium">Welcome to the</h3>
                <h1 className="mb-2 text-white fw-bold">Euflexxa Solution Center</h1>
              </div>

              <div className="card-body">
                
                <div className="text-danger">
                  <h6 className="fw-bold h6">Scheduled Network Maintenance Notice</h6>
                  <p>
                    One of our technology partners will be performing routine, scheduled maintenance <b>August 25–27 from
                    5:30–8:30 AM ET</b>. Brief service interruptions may occur during this time. No action is required.
                  </p>
                </div>

                <label htmlFor="email" className="form-label justify-content-start mb-2">
                  Email *
                </label>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  render={({field, fieldState: {error}}) => (
                    <HubInputField
                      inputId="email"
                      inputPlaceholder="Email"
                      isRowBased={false}
                      isRequired={true}
                      field={field}
                      error={error}
                      inputType="email"
                    />
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  render={({field, fieldState: {error}}) => (
                    <>
                      <label htmlFor="password" className="form-label justify-content-start mb-2">
                        Password *
                      </label>
                      <div className="input-group mb-3">
                        <input
                          type={isPasswordVisible ? "text" : "password"}
                          placeholder="Password"
                          className="form-control" {...field}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-dark"
                          onClick={() => setPasswordVisibility((isPasswordVisible) => !isPasswordVisible)}
                        >
                          {isPasswordVisible ? (
                            <i className="bi bi-eye-fill"></i>
                          ) : (
                            <i className="bi bi-eye-slash"></i>
                          )}
                        </button>
                      </div>

                      {error && (<p className="mb-0 text-danger">{error.message}</p>)}
                    </>
                  )}
                />
                <div className="mb-0">
                  <button disabled={loading} className="btn btn-primary" type="submit">
                    {loading ? (<i className="spinner-border"></i>) : null} Log in
                  </button>
                  <button type="button" className="btn btn-tertiary ms-1" onClick={() => navigate("/enrollments/new")}>
                    Enroll
                  </button>
                  <p className="m-0">
                    <Link to="/users/forgotpassword" className="text-decoration-none btn-link">
                      Forgot your password?
                    </Link>
                  </p>
                </div>
              </div>
            </form>

            <h1 className="fw-bold text-white pt-4 text-center">
              Preferred HA on UHC Commercial Plans<sup>3</sup>
            </h1>
          </div>
        </div>

        <div className="text-white"><FooterText footerType="black"/></div>
      </div>
    </div>
  );
};

export default HubLogin;
